import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { DATA_RETENTION_DAYS } from '@/shared/utils/plan-tab-model';

interface Props {
  /** Already-formatted renewal date, or empty when unknown. */
  renewalDate: string;
  isCancelled: boolean;
  onCancel: () => void;
  onReactivate: () => void;
  /** The owner asked to cancel and the access runs to the renewal date (BUG-048). */
  scheduled?: boolean;
  /** Withdraws the scheduled cancellation. */
  onKeep?: () => void;
  /** In the free trial cancelling charges nothing (RN-11). */
  trialing?: boolean;
}

const LINK_STYLE = {
  fontSize: 'var(--text-meta)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--accent-cobre)',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  whiteSpace: 'nowrap',
} as const;

/** Which sentence precedes the link: the trial one says nothing is charged. */
function sentenceKey(renewalDate: string, trialing: boolean): string {
  if (!renewalDate) return 'subscription.plan.cancel.sentenceNoDate';
  return trialing
    ? 'subscription.plan.cancel.sentenceTrial'
    : 'subscription.plan.cancel.sentence';
}

/**
 * The cancellation footer.
 *
 * A discreet link rather than a red destructive button, preceded by what
 * cancelling actually costs. Not hidden either — whoever looks for it finds it.
 *
 * @param props - Component props.
 * @returns The footer.
 */
export function CancellationFooter({
  renewalDate, isCancelled, onCancel, onReactivate, scheduled = false, onKeep, trialing = false,
}: Props) {
  const { t } = useTranslation('features/pricing');

  if (scheduled) {
    return (
      <CancellationLine
        sentence={t('subscription.plan.cancel.accessUntil', { date: renewalDate || '—' })}
        action={t('subscription.plan.cancel.keep')}
        testId="keep-subscription-link"
        onAction={onKeep ?? (() => undefined)}
      />
    );
  }

  const sentence = isCancelled
    ? t('subscription.plan.cancel.scheduled', { date: renewalDate || '—' })
    : t(sentenceKey(renewalDate, trialing), { date: renewalDate, days: DATA_RETENTION_DAYS });

  return (
    <CancellationLine
      sentence={sentence}
      action={t(isCancelled
        ? 'subscription.plan.cancel.reactivate'
        : 'subscription.plan.cancel.link')}
      testId={isCancelled ? 'reactivate-link' : 'request-cancellation-button'}
      onAction={isCancelled ? onReactivate : onCancel}
    />
  );
}

/** The footer's one line: what happens, then the link that acts on it. */
function CancellationLine({ sentence, action, testId, onAction }: {
  sentence: string; action: string; testId: string; onAction: () => void;
}) {
  return (
    <div
      className="flex items-center justify-between gap-(--space-4) flex-wrap"
      data-testid="cancellation-footer"
      style={{
        borderTop: '1px solid var(--border-subtle)',
        paddingTop: 'var(--space-4)',
      }}
    >
      <Text component="span" className="min-w-0" style={{
        fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)',
        lineHeight: 'var(--leading-normal)', flex: '1 1 28rem',
      }}>
        {sentence}
      </Text>
      <UnstyledButton onClick={onAction} data-testid={testId} style={LINK_STYLE}>
        {action}
      </UnstyledButton>
    </div>
  );
}
